import React, { useState, useEffect, useRef } from "react";
import logo from "../../icon/logo.png";
import supabase from "../../supabase";

/* ─────────────────────────────────────────────
   DESIGN TOKENS  (identical to login.jsx)
───────────────────────────────────────────── */
const C = {
  bg:         "#F3F6FA",
  surface:    "#FFFFFF",
  surfaceAlt: "#E9EFF5",
  border:     "#D8E2EC",
  borderFocus:"#0E7C5A60",
  borderHover:"#B9C9DA",
  accent:     "#0E7C5A",
  accentDim:  "rgba(14,124,90,0.08)",
  accentGlow: "rgba(14,124,90,0.18)",
  text:       "#16283F",
  textMid:    "#4C6178",
  textDim:    "#8C9BAC",
  red:        "#C0392B",
  green:      "#16915F",
  blue:       "#2F6690",
};

/* ─────────────────────────────────────────────
   GLOBAL CSS  (shared with login.jsx verbatim)
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html { scroll-behavior: smooth; height: 100%; }

body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

a { text-decoration: none; color: inherit; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes gridFade {
  from { opacity: 0; }
  to   { opacity: .3; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.5; transform:scale(.9); }
}
@keyframes spinDot {
  to { transform: rotate(360deg); }
}
@keyframes shake {
  0%,100% { transform: translateX(0); }
  20%,60% { transform: translateX(-6px); }
  40%,80% { transform: translateX(6px); }
}
@keyframes successPop {
  0%   { transform: scale(.8); opacity:0; }
  60%  { transform: scale(1.08); }
  100% { transform: scale(1); opacity:1; }
}

.auth-card-enter   { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .05s both; }
.auth-header-enter { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .15s both; }

.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 40%, black 0%, transparent 100%);
}

/* ── Nav ── */
.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}
.tf-nav-actions {
  display: flex; align-items: center; gap: 12px; min-width: 0;
}
.tf-nav-cta-label { font-size: 13px; color: ${C.textDim}; white-space: nowrap; }
.tf-nav-cta {
  font-size: 12px; padding: 8px 16px; border-radius: 8px;
  background: ${C.accentDim}; border: 1px solid ${C.accent}40;
  color: ${C.accent}; font-weight: 700; letter-spacing: .01em;
  transition: background .2s, color .2s; white-space: nowrap; flex-shrink: 0;
}
@media (max-width: 480px) {
  .tf-nav-actions { gap: 8px; }
  .tf-nav-cta-label { display: none; }
  .tf-nav-cta { font-size: 11px; padding: 7px 11px; }
}

/* ── iOS PWA safe area ── */
@supports (padding-top: env(safe-area-inset-top)) {
  .tf-nav {
    padding-top: env(safe-area-inset-top);
    height: calc(60px + env(safe-area-inset-top));
  }
  .tf-nav-offset {
    min-height: calc(100vh - 60px - env(safe-area-inset-top));
  }
}

/* ── Input ── */
.tf-input {
  width: 100%;
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px; color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  outline: none;
  transition: border-color .2s, box-shadow .2s;
}
.tf-input:focus {
  border-color: ${C.accent}60;
  box-shadow: 0 0 0 3px ${C.accentDim};
}
.tf-input::placeholder { color: ${C.textDim}; }
.tf-input.error {
  border-color: ${C.red}70;
  box-shadow: 0 0 0 3px rgba(192,57,43,.08);
}
.tf-input.success { border-color: ${C.accent}60; }

/* ── Password input wrapper ── */
.pw-wrap { position: relative; }
.pw-wrap .tf-input { padding-right: 46px; }
.pw-toggle {
  position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: ${C.textDim}; cursor: pointer;
  font-size: 17px; padding: 4px; line-height: 1;
  transition: color .15s;
}
.pw-toggle:hover { color: ${C.textMid}; }

/* ── OTP inputs ── */
.otp-grid { display: grid; grid-template-columns: repeat(8,1fr); gap: 8px; }
.otp-cell {
  aspect-ratio: 1;
  background: ${C.surfaceAlt}; border: 1px solid ${C.border};
  border-radius: 10px;
  font-family: 'IBM Plex Mono', monospace; font-size: 20px; font-weight: 500;
  color: ${C.text}; text-align: center;
  outline: none; width: 100%;
  transition: border-color .2s, box-shadow .2s; padding: 0;
}
.otp-cell:focus {
  border-color: ${C.accent}60;
  box-shadow: 0 0 0 3px ${C.accentDim};
}
.otp-cell.filled { border-color: ${C.accent}50; color: ${C.accent}; }
.otp-cell.otp-error { border-color: ${C.red}70; animation: shake .35s ease; }

/* ── Step dots ── */
.step-dots { display:flex; align-items:center; gap:6px; justify-content:center; margin-bottom:20px; }
.step-dot {
  width:7px; height:7px; border-radius:50%;
  background:${C.border}; transition:background .25s, transform .25s;
}
.step-dot.active { background:${C.accent}; transform:scale(1.3); }
.step-dot.done   { background:${C.accent}40; }

/* ── Step connector line ── */
.step-connector {
  width: 24px; height: 1px; background: ${C.border};
}

/* ── Error / info message ── */
.field-error { font-size:12px; color:${C.red}; margin-top:5px; display:flex; align-items:center; gap:4px; }
.field-info  { font-size:12px; color:${C.textMid}; margin-top:5px; display:flex; align-items:center; gap:4px; }

/* ── Success icon ── */
.success-icon {
  width:64px; height:64px; border-radius:50%;
  background:${C.accentDim}; border:1px solid ${C.accent}40;
  display:flex; align-items:center; justify-content:center;
  font-size:28px; margin:0 auto 20px;
  animation: successPop .5s cubic-bezier(.22,1,.36,1) both;
}

/* ── Spinner ── */
.spinner {
  width:18px; height:18px; border-radius:50%;
  border:2px solid rgba(255,255,255,.3); border-top-color:#F3FAF7;
  animation: spinDot .7s linear infinite; flex-shrink:0;
}
.spinner-accent {
  width:36px; height:36px; border-radius:50%;
  border:2px solid ${C.border}; border-top-color:${C.accent};
  animation: spinDot .8s linear infinite;
}

/* ── Badge live ── */
.badge-live {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(14,124,90,.08); border:1px solid rgba(14,124,90,.2);
  border-radius:6px; padding:3px 8px;
  font-family:'IBM Plex Mono',monospace; font-size:10px; color:${C.accent};
}
.badge-live::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:${C.accent}; animation:pulse 1.5s ease-in-out infinite; flex-shrink:0;
}

/* ── Buttons ── */
.btn-primary {
  width: 100%; padding: 14px;
  background: ${C.accent}; color: #F3FAF7;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700;
  font-family: 'Public Sans', sans-serif;
  cursor: pointer; letter-spacing: .01em;
  transition: transform .18s, box-shadow .18s, background .18s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  position: relative; overflow: hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background: linear-gradient(135deg,rgba(255,255,255,.16) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 10px 36px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; transform:none; }

.btn-ghost {
  width: 100%; padding: 13px;
  background: transparent; color: ${C.textMid};
  border: 1px solid ${C.border}; border-radius: 10px;
  font-size: 14px; font-weight: 600;
  font-family: 'Public Sans', sans-serif;
  cursor: pointer;
  transition: border-color .2s, color .2s, background .2s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.btn-ghost:hover:not(:disabled) { border-color:${C.borderHover}; color:${C.text}; background:rgba(22,40,63,.04); }
.btn-ghost:disabled { opacity:.4; cursor:not-allowed; }

/* ── Resend row ── */
.resend-row {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 13px; color: ${C.textDim}; margin-top: 18px;
}
.resend-btn {
  background: none; border: none; cursor: pointer;
  color: ${C.accent}; font-size: 13px; font-weight: 600;
  font-family: 'Public Sans', sans-serif; padding: 0;
}
.resend-btn:disabled { opacity:.45; cursor:not-allowed; }

/* ── Strength bar ── */
.strength-bar-track {
  height: 3px; border-radius: 99px; background: ${C.border}; margin-top: 8px; overflow: hidden;
}
.strength-bar-fill {
  height: 100%; border-radius: 99px;
  transition: width .3s ease, background .3s ease;
}

/* ── Password requirements list ── */
.pw-reqs {
  display: flex; flex-direction: column; gap: 5px; margin-top: 10px;
}
.pw-req {
  display: flex; align-items: center; gap: 7px;
  font-size: 11.5px; color: ${C.textDim};
  transition: color .2s;
}
.pw-req.met { color: ${C.accent}; }
.pw-req-dot {
  width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0;
}

/* ── Scrollbar ── */
::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Language switcher ── */
.lang-switcher { position:relative; display:inline-flex; align-items:center; }
.lang-btn {
  display:inline-flex; align-items:center; gap:6px;
  background:transparent; border:1px solid ${C.border};
  border-radius:8px; padding:6px 10px;
  font-size:12px; font-weight:600; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:border-color .2s, color .2s, background .2s; white-space:nowrap;
}
.lang-btn:hover { border-color:${C.borderHover}; color:${C.text}; background:rgba(22,40,63,.04); }
.lang-dropdown {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:12px; padding:6px; min-width:180px; z-index:400;
  box-shadow:0 16px 40px rgba(22,40,63,.16); animation:fadeUp .2s ease both;
}
.lang-option {
  display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:8px;
  font-size:13px; color:${C.textMid}; cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:background .15s, color .15s;
}
.lang-option:hover { background:${C.surfaceAlt}; color:${C.text}; }
.lang-option.active { color:${C.accent}; background:rgba(0,229,160,0.10); }

.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
body { top:0 !important; }
.skiptranslate { display:none !important; }
.notranslate, .notranslate * { translate: no; }

/* ── Responsive ── */
@media (max-width: 767px) { .side-panel-desktop { display: none !important; } }
@media (max-width: 480px) {
  .otp-cell { font-size: 16px; }
  .otp-grid { gap: 6px; }
}
@media (max-width: 360px) {
  .otp-cell { font-size: 14px; border-radius: 8px; }
  .otp-grid { gap: 4px; }
}
@media (min-width: 768px) and (max-width: 1024px) {
  .side-panel-desktop { max-width: 340px !important; }
}
`;

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER  (identical to login.jsx)
───────────────────────────────────────────── */
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => { setCurrent(code); localStorage.setItem("tf_lang", code); setOpen(false); applyGoogleTranslate(code); };
  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{fontSize:"14px"}}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{fontSize:"9px", opacity:.6, marginLeft:"2px"}}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{fontSize:"10px", fontWeight:"600", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px"}}>Select Language</div>
          {LANGUAGES.map(l => (
            <div key={l.code} className={"lang-option" + (l.code === current ? " active" : "")} onClick={() => select(l.code)}>
              <span style={{fontSize:"16px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{marginLeft:"auto", fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   ICON HELPERS
───────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:.5}}>
    <rect x="2" y="4" width="20" height="16" rx="3"/><path d="m22 7-10 7L2 7"/>
  </svg>
);
const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:.5}}>
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

/* ─────────────────────────────────────────────
   FIELD WRAPPER
───────────────────────────────────────────── */
const Field = ({ label, hint, error, info, children }) => (
  <div style={{ marginBottom: "20px" }}>
    {(label || hint) && (
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"7px" }}>
        <label style={{ fontSize:"13px", fontWeight:"600", color:C.textMid, letterSpacing:".01em" }}>{label}</label>
        {hint}
      </div>
    )}
    {children}
    {error && <div className="field-error">⚠ {error}</div>}
    {info && !error && <div className="field-info">ℹ {info}</div>}
  </div>
);

/* ─────────────────────────────────────────────
   OTP INPUT  (6-cell, auto-advance)
───────────────────────────────────────────── */
const OTPInput = ({ value, onChange, hasError }) => {
  const refs   = useRef(Array.from({ length: 8 }, () => React.createRef()));
  const digits = value.split("").concat(Array(8).fill("")).slice(0, 8);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, -1);
      onChange(next);
      if (i > 0) refs.current[i - 1].current?.focus();
      return;
    }
    if (/^\d$/.test(e.key)) {
      const next = (value + e.key).slice(0, 8);
      onChange(next);
      if (i < 7) refs.current[i + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 8);
    onChange(pasted);
    refs.current[Math.min(pasted.length, 7)].current?.focus();
    e.preventDefault();
  };

  return (
    <div className="otp-grid">
      {digits.map((d, i) => (
        <input
          key={i} ref={refs.current[i]}
          className={`otp-cell${d ? " filled" : ""}${hasError ? " otp-error" : ""}`}
          type="text" inputMode="numeric" maxLength={1}
          value={d} readOnly
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={() => refs.current[i].current?.select()}
        />
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   PASSWORD STRENGTH METER
───────────────────────────────────────────── */
function usePasswordStrength(pw) {
  const reqs = [
    { label:"At least 8 characters",         met: pw.length >= 8 },
    { label:"One uppercase letter",           met: /[A-Z]/.test(pw) },
    { label:"One number",                     met: /\d/.test(pw) },
    { label:"One special character (!@#…)",   met: /[^A-Za-z0-9]/.test(pw) },
  ];
  const score = reqs.filter(r => r.met).length;
  const width  = score === 0 ? "0%" : score === 1 ? "25%" : score === 2 ? "50%" : score === 3 ? "75%" : "100%";
  const color  = score <= 1 ? C.red : score === 2 ? "#D68910" : score === 3 ? C.blue : C.accent;
  const label  = score === 0 ? "" : score === 1 ? "Weak" : score === 2 ? "Fair" : score === 3 ? "Good" : "Strong";
  return { reqs, score, width, color, label };
}

/* ─────────────────────────────────────────────
   STEP PROGRESS INDICATOR
───────────────────────────────────────────── */
const StepProgress = ({ current, total }) => (
  <div className="step-dots">
    {Array.from({ length: total }).map((_, i) => (
      <React.Fragment key={i}>
        <div className={`step-dot${i + 1 === current ? " active" : i + 1 < current ? " done" : ""}`} />
        {i < total - 1 && <div className="step-connector" />}
      </React.Fragment>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   STEP 1 — Enter email
───────────────────────────────────────────── */
const StepEmail = ({ onNext }) => {
  const [email,   setEmail]   = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email.includes("@")) { setError("Enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (err) throw err;
      onNext({ email });
    } catch (err) {
      const msg = err.message?.toLowerCase() ?? "";
      if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        // Don't reveal if email exists or not for security — always advance
        onNext({ email });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{
        width:52, height:52, borderRadius:14,
        background:C.accentDim, border:`1px solid ${C.accent}30`,
        display:"flex", alignItems:"center", justifyContent:"center",
        margin:"0 auto 22px",
      }}>
        <MailIcon />
      </div>

      <Field label="Email address" error={error}>
        <input
          className={`tf-input${error ? " error" : email.includes("@") ? " success" : ""}`}
          type="email" placeholder="alex@email.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && submit()}
          autoComplete="email" autoFocus
        />
      </Field>

      <button className="btn-primary" onClick={submit} disabled={loading}>
        {loading ? <><div className="spinner" /> Sending…</> : <>Send reset code <span style={{fontSize:"16px"}}>→</span></>}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 2 — Verify OTP
───────────────────────────────────────────── */
const StepOTP = ({ email, onNext, onBack }) => {
  const [otp,      setOtp]      = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [resendCd, setResendCd] = useState(45);
  const [resending,setResending]= useState(false);

  // Countdown timer
  useEffect(() => {
    if (resendCd <= 0) return;
    const t = setTimeout(() => setResendCd(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCd]);

  const submit = async () => {
    if (otp.length < 8) { setError("Enter the 8-digit code from your email."); return; }
    setError(""); setLoading(true);
    try {
      const { error: err } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "recovery",
      });
      if (err) throw err;
      onNext();
    } catch (err) {
      const msg = err.message?.toLowerCase() ?? "";
      if (msg.includes("expired")) {
        setError("This code has expired. Request a new one below.");
      } else if (msg.includes("invalid")) {
        setError("Incorrect code. Double-check and try again.");
      } else {
        setError(err.message || "Verification failed. Please try again.");
      }
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
      });
      setResendCd(45);
      setError("");
      setOtp("");
    } finally {
      setResending(false);
    }
  };

  return (
    <div>
      <div style={{
        width:52, height:52, borderRadius:14,
        background:"rgba(47,102,144,0.10)", border:`1px solid rgba(47,102,144,.25)`,
        display:"flex", alignItems:"center", justifyContent:"center",
        margin:"0 auto 22px", fontSize:22,
      }}>
        📬
      </div>

      <p style={{fontSize:"13px", color:C.textMid, textAlign:"center", lineHeight:"1.65", marginBottom:"24px"}}>
        We sent an 8-digit code to{" "}
        <span style={{color:C.text, fontWeight:"600"}}>{email}</span>.
        {" "}Check your inbox (and spam folder).
      </p>

      <Field label="Verification code" error={error}>
        <OTPInput value={otp} onChange={v => { setOtp(v); setError(""); }} hasError={!!error} />
      </Field>

      <button className="btn-primary" onClick={submit} disabled={loading || otp.length < 8} style={{marginBottom:12}}>
        {loading ? <><div className="spinner" /> Verifying…</> : <>Confirm code <span style={{fontSize:"16px"}}>→</span></>}
      </button>

      <button className="btn-ghost" onClick={onBack} disabled={loading}>← Back</button>

      <div className="resend-row">
        {resendCd > 0
          ? <span>Resend code in <span style={{color:C.textMid, fontFamily:"'IBM Plex Mono',monospace"}}>{resendCd}s</span></span>
          : <>
              <span>Didn't get it?</span>
              <button className="resend-btn" onClick={resend} disabled={resending}>
                {resending ? "Sending…" : "Resend code"}
              </button>
            </>
        }
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 3 — New password
───────────────────────────────────────────── */
const StepNewPassword = ({ onNext }) => {
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [showPw,    setShowPw]    = useState(false);
  const [showCf,    setShowCf]    = useState(false);
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const strength = usePasswordStrength(password);

  const validate = () => {
    const e = {};
    if (strength.score < 2)           e.password = "Choose a stronger password.";
    if (password !== confirm)          e.confirm  = "Passwords don't match.";
    if (!confirm)                      e.confirm  = "Please confirm your password.";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({}); setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      onNext();
    } catch (err) {
      setErrors({ password: err.message || "Failed to update password. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{
        width:52, height:52, borderRadius:14,
        background:"rgba(0,229,160,0.08)", border:`1px solid ${C.accent}25`,
        display:"flex", alignItems:"center", justifyContent:"center",
        margin:"0 auto 22px",
      }}>
        <LockIcon />
      </div>

      <Field label="New password" error={errors.password}>
        <div className="pw-wrap">
          <input
            className={`tf-input${errors.password ? " error" : password && strength.score >= 3 ? " success" : ""}`}
            type={showPw ? "text" : "password"}
            placeholder="Create a strong password"
            value={password}
            onChange={e => { setPassword(e.target.value); setErrors(p => ({...p, password:""})); }}
            onKeyDown={e => e.key === "Enter" && submit()}
            autoComplete="new-password" autoFocus
          />
          <button type="button" className="pw-toggle" onClick={() => setShowPw(s => !s)}>
            {showPw ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>

        {/* Strength bar */}
        {password && (
          <>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8}}>
              <div className="strength-bar-track" style={{flex:1}}>
                <div className="strength-bar-fill" style={{width:strength.width, background:strength.color}} />
              </div>
              <span style={{fontSize:"11px", color:strength.color, fontWeight:"600", marginLeft:10, minWidth:40, textAlign:"right"}}>
                {strength.label}
              </span>
            </div>
            <div className="pw-reqs">
              {strength.reqs.map((r, i) => (
                <div key={i} className={`pw-req${r.met ? " met" : ""}`}>
                  <div className="pw-req-dot" />
                  {r.label}
                </div>
              ))}
            </div>
          </>
        )}
      </Field>

      <Field label="Confirm password" error={errors.confirm}>
        <div className="pw-wrap">
          <input
            className={`tf-input${errors.confirm ? " error" : confirm && confirm === password ? " success" : ""}`}
            type={showCf ? "text" : "password"}
            placeholder="Repeat your new password"
            value={confirm}
            onChange={e => { setConfirm(e.target.value); setErrors(p => ({...p, confirm:""})); }}
            onKeyDown={e => e.key === "Enter" && submit()}
            autoComplete="new-password"
          />
          <button type="button" className="pw-toggle" onClick={() => setShowCf(s => !s)}>
            {showCf ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </Field>

      <button className="btn-primary" onClick={submit} disabled={loading || strength.score < 2 || !confirm}>
        {loading ? <><div className="spinner" /> Updating…</> : <>Update password <span style={{fontSize:"16px"}}>→</span></>}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 4 — Success
───────────────────────────────────────────── */
const StepSuccess = () => {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = "/login"; }, 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{textAlign:"center", padding:"12px 0 8px"}}>
      <div className="success-icon">✓</div>
      <h3 style={{fontFamily:"'Fraunces',serif", fontSize:"20px", fontWeight:"800", color:C.text, marginBottom:"8px"}}>
        Password updated!
      </h3>
      <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.65", marginBottom:"24px"}}>
        Your account is secure. Redirecting you to sign in…
      </p>
      <div style={{display:"flex", justifyContent:"center"}}>
        <div className="spinner-accent" />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SIDE PANEL  (identical to login.jsx)
───────────────────────────────────────────── */
const SidePanel = () => {
  const items = [
    { label:"Portfolio", val:"$48,291",  sub:"+28.4% this month", color:C.green },
    { label:"Copying",   val:"3 Traders", sub:"Auto-rebalanced",  color:C.blue  },
    { label:"Win Rate",  val:"76.4%",    sub:"Last 90 days",      color:C.accent},
  ];
  return (
    <div style={{
      position:"relative", flex:"1",
      background:`linear-gradient(160deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
      borderRight:`1px solid ${C.border}`,
      padding:"48px 36px",
      display:"flex", flexDirection:"column", justifyContent:"space-between",
      overflow:"hidden", minHeight:"calc(100vh - 60px - env(safe-area-inset-top, 0px))",
    }}>
      <div style={{position:"absolute", width:"380px", height:"380px", borderRadius:"50%", background:C.accentGlow, filter:"blur(90px)", top:"-100px", left:"-80px", pointerEvents:"none"}}/>
      <div style={{position:"absolute", width:"200px", height:"200px", borderRadius:"50%", background:"rgba(47,102,144,.12)", filter:"blur(60px)", bottom:"80px", right:"-40px", pointerEvents:"none"}}/>

      <div style={{position:"relative", zIndex:1}}>
        <a href="/" style={{display:"inline-flex", alignItems:"center", marginBottom:"40px"}}>
          <img src={logo} alt="Optionstock" style={{height:"38px", width:"auto"}} />
        </a>
        <div style={{marginBottom:"12px"}}>
          <div className="badge-live" style={{marginBottom:"16px"}}>Markets Open</div>
          <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,2.5vw,30px)", fontWeight:"800", letterSpacing:"-1px", color:C.text, lineHeight:"1.2", marginBottom:"12px"}}>
            Your portfolio<br/>is waiting.
          </h2>
          <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.7", maxWidth:"280px"}}>
            Reset your password and get back to monitoring live positions, copy signals, and your traders.
          </p>
        </div>
      </div>

      <div style={{position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:"12px"}}>
        {items.map((it,i)=>(
          <div key={i} style={{
            background:"rgba(255,255,255,.75)", border:`1px solid ${C.border}`,
            borderRadius:"12px", padding:"16px 18px",
            display:"flex", justifyContent:"space-between", alignItems:"center",
            backdropFilter:"blur(8px)",
            animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.4+i*.12}s both`,
          }}>
            <div>
              <div style={{fontSize:"11px", color:C.textDim, marginBottom:"4px", fontWeight:"600", letterSpacing:".06em", textTransform:"uppercase"}}>{it.label}</div>
              <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"18px", color:it.color, fontWeight:"500"}}>{it.val}</div>
            </div>
            <div style={{fontSize:"11px", color:C.textDim, textAlign:"right"}}>{it.sub}</div>
          </div>
        ))}
        <p style={{fontSize:"11px", color:C.textDim, textAlign:"center", marginTop:"6px"}}>
          Simulated data for illustration only
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP HEADERS
───────────────────────────────────────────── */
const STEP_META = {
  1: { title:"Forgot password?",    sub:"Enter your email and we'll send a reset code." },
  2: { title:"Check your inbox",    sub:"Enter the 8-digit code we sent you." },
  3: { title:"Create new password", sub:"Choose something secure and memorable." },
  4: { title:"You're all set!",     sub:"" },
};

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
const ForgotPassword = () => {
  const [step,  setStep]  = useState(1);
  const [email, setEmail] = useState("");

  const TOTAL = 3; // step 4 (success) isn't counted in progress dots

  return (
    <>
      <style>{CSS}</style>

      {/* NAV */}
      <nav className="tf-nav">
        <a href="/" style={{display:"flex", alignItems:"center", flexShrink:0}}>
          <img src={logo} alt="Optionstock" style={{height:"38px", width:"auto"}} />
        </a>
        <div className="tf-nav-actions">
          <LanguageSwitcher />
          <span className="tf-nav-cta-label">Remember it now?</span>
          <a href="/login" className="tf-nav-cta">Sign in →</a>
        </div>
      </nav>

      {/* GRID BG */}
      <div className="grid-bg" />

      {/* LAYOUT */}
      <div className="tf-nav-offset" style={{
        minHeight:"calc(100vh - 60px)", display:"flex",
        position:"relative", zIndex:1, alignItems:"stretch",
      }}>

        {/* SIDE PANEL */}
        <div className="side-panel-desktop" style={{display:"none", flex:"1", maxWidth:"480px"}}>
          <SidePanel />
        </div>
        <style>{`@media(min-width:768px){ .side-panel-desktop{ display:flex !important; } }`}</style>

        {/* FORM PANEL */}
        <div style={{
          flex:"1", display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"clamp(32px,6vw,64px) clamp(16px,5vw,48px)",
        }}>
          <div style={{width:"100%", maxWidth:"400px"}}>

            {/* Header */}
            <div className="auth-header-enter" style={{marginBottom:"28px", textAlign:"center"}}>
              {step < 4 && <StepProgress current={step} total={TOTAL} />}
              <h1 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,3vw,28px)", fontWeight:"800", letterSpacing:"-0.5px", color:C.text, marginBottom:"6px"}}>
                {STEP_META[step].title}
              </h1>
              {STEP_META[step].sub && (
                <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.6"}}>
                  {STEP_META[step].sub}
                </p>
              )}
            </div>

            {/* Card */}
            <div className="auth-card-enter notranslate" translate="no" style={{
              background:C.surface,
              border:`1px solid ${C.border}`,
              borderRadius:"18px",
              padding:"clamp(20px,4vw,32px)",
              boxShadow:`0 32px 64px rgba(22,40,63,.14), 0 0 0 1px ${C.border}`,
            }}>
              {step === 1 && (
                <StepEmail onNext={({ email: e }) => { setEmail(e); setStep(2); }} />
              )}
              {step === 2 && (
                <StepOTP
                  email={email}
                  onNext={() => setStep(3)}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && (
                <StepNewPassword onNext={() => setStep(4)} />
              )}
              {step === 4 && <StepSuccess />}
            </div>

            {/* Footer */}
            {step < 4 && (
              <div style={{textAlign:"center", marginTop:"24px", fontSize:"13px", color:C.textDim}}>
                Remember your password?{" "}
                <a href="/login" style={{color:C.accent, fontWeight:"600"}}>Sign in →</a>
              </div>
            )}

            {/* Legal */}
            <p style={{textAlign:"center", fontSize:"11px", color:C.textDim, marginTop:"20px", lineHeight:"1.6"}}>
              By continuing you agree to our{" "}
              <a href="/terms" style={{color:C.textDim, textDecoration:"underline"}}>Terms</a>
              {" "}and{" "}
              <a href="/privacy" style={{color:C.textDim, textDecoration:"underline"}}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;